import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import HeaderAuth from "@/components/HeaderAuth";
import SectionLink from "@/components/powlo/SectionLink";

const SCROLL_TOP_HREFS = new Set([
  "/methodology",
  "/score",
  "/about",
  "/blog",
  "/faq",
  "/contact",
  "/privacy",
  "/terms",
  "/cookies",
]);

const navLinks = [
  { href: "/#how", label: "How it works" },
  { href: "/#example", label: "Example plan" },
  { href: "/score", label: "Home Power Score" },
  { href: "/about", label: "Why Powlo" },
  { href: "/blog", label: "Insights" },
];

const desktopLinkClass = "navlink text-[#C9D1DE] no-underline";
const menuLinkClass = "navlink flex min-h-11 items-center rounded-2xl px-4 text-[15px] font-semibold text-[#C9D1DE] no-underline hover:bg-white/10";
const signInClassName = "flex h-10 items-center whitespace-nowrap rounded-full border border-white/20 px-4 text-sm font-semibold text-white hover:border-powlo-lime hover:text-powlo-lime";

export default function PowloHeader() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || navRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[role='dialog']")) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <header className="sticky top-5 z-50 w-full px-5 sm:px-8 lg:px-20">
      <nav
        ref={navRef}
        aria-label="Main"
        className="relative mx-auto flex w-full items-center justify-between gap-3 rounded-full border border-white/10 bg-[#061525]/95 py-3 pl-6 pr-3 shadow-[0_8px_30px_rgba(0,0,0,0.25)] backdrop-blur-[16px]"
      >
        <Link
          href="/"
          aria-label="Powlo home"
          className="flex shrink-0 items-center"
          onClick={(event) => {
            if (location !== "/") return;
            event.preventDefault();
            scrollToPageTop();
            window.history.replaceState(null, "", "/");
          }}
        >
          <img src="/powlo-logo-lime.svg" alt="Powlo" className="h-10 w-auto" />
        </Link>
        <div className="hidden gap-6 whitespace-nowrap text-[15px] font-semibold xl:flex">
          {navLinks.map((link) => (
            <NavItem key={link.href} href={link.href} className={desktopLinkClass}>
              {link.label}
            </NavItem>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="tel:+442046202179"
            aria-label="Call Powlo: 020 4620 2179"
            className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 text-[#C9D1DE] hover:border-powlo-lime hover:text-powlo-lime sm:flex"
          >
            <PhoneIcon />
          </a>
          <div className="hidden xl:block">
            <HeaderAuth tone="dark" signInClassName={signInClassName} />
          </div>
          <SectionLink
            href="/#check"
            className="hidden h-11 items-center whitespace-nowrap rounded-full border-[1.5px] border-ink bg-lime px-5 text-sm font-bold text-ink no-underline sm:text-base xl:flex"
          >
            Design my system
          </SectionLink>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white xl:hidden"
            aria-expanded={open}
            aria-controls="powlo-mobile-nav"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((current) => !current)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
        {open && (
          <div
            id="powlo-mobile-nav"
            className="absolute left-0 right-0 top-[calc(100%+8px)] flex flex-col gap-1 rounded-3xl border border-white/10 bg-[#061525] p-3 shadow-[0_8px_30px_rgba(0,0,0,0.25)] xl:hidden"
            onClick={() => setOpen(false)}
          >
            {navLinks.map((link) => (
              <NavItem key={link.href} href={link.href} className={menuLinkClass}>
                {link.label}
              </NavItem>
            ))}
            <div onClick={(event) => event.stopPropagation()}>
              <HeaderAuth tone="dark" signInClassName={`${menuLinkClass} w-full justify-start border-0`} />
            </div>
            <SectionLink
              href="/#check"
              className="mt-1 flex h-11 items-center justify-center rounded-full border-[1.5px] border-ink bg-lime px-5 text-sm font-bold text-ink no-underline"
            >
              Design my system
            </SectionLink>
          </div>
        )}
      </nav>
    </header>
  );
}

function NavItem({ href, className, children }: { href: string; className: string; children: React.ReactNode }) {
  if (href.includes("#")) {
    return (
      <SectionLink href={href} className={className}>
        {children}
      </SectionLink>
    );
  }
  return (
    <Link href={href} className={className} onClick={SCROLL_TOP_HREFS.has(href) ? scrollToPageTop : undefined}>
      {children}
    </Link>
  );
}

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}
